import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useState } from "react";

import type { FolderOption } from "./folderOptions.js";
import { FolderSelect } from "./FolderSelect.js";

/**
 * Move one or many documents into another folder or sub-folder
 * (2026-09-23). Only real folders are offered: portal-api drops a null
 * `document_category_id` on update, so "move to Uncategorized" could not
 * be honoured and is not pretended.
 */
export function MoveToFolderDialog({
  open,
  count,
  folders,
  currentFolderId,
  onClose,
  onMove,
}: {
  open: boolean;
  count: number;
  folders: FolderOption[];
  /** The folder the documents are in now — moving there is a no-op. */
  currentFolderId: number | null;
  onClose: () => void;
  onMove: (folderId: number) => Promise<void>;
}): JSX.Element {
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTarget("");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const same = target !== "" && Number(target) === currentFolderId;
  const noun = count === 1 ? "document" : `${count} documents`;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Move {noun}</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 1.5, pt: "8px !important" }}>
        <FolderSelect
          folders={folders}
          value={target}
          onChange={setTarget}
          label="Move to"
          emptyLabel="Pick a folder…"
          {...(same ? { helperText: "Already in this folder" } : {})}
        />
        {error && (
          <Box
            role="alert"
            sx={{ borderRadius: 1, bgcolor: (t) => alpha(t.palette.error.main, 0.1), px: 1.5, py: 1, fontSize: 12, color: "error.main" }}
          >
            {error}
          </Box>
        )}
        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
          Who can read a document follows its new folder&rsquo;s portal settings.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          data-testid="move-confirm"
          disabled={target === "" || same || busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await onMove(Number(target));
              onClose();
            } catch (err) {
              setError(err instanceof Error ? err.message : "Move failed");
              setBusy(false);
            }
          }}
        >
          {busy ? "Moving…" : "Move"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
